/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import moment from 'moment';

import config from 'js/app/config';
import Imgix from 'js/components/Imgix';
import { get as getUser } from 'js/lib/user';

import { Grid, PageGridContainer, Skeleton, Typography2, breakpoints } from '@coursera/cds-core';

import _t from 'i18n!nls/onboarding-2022';

type Props = {
  isMobile: boolean;
};
const styles = {
  mainBodyGrid: css`
    margin: 0 var(--cds-spacing-200) var(--cds-spacing-400) var(--cds-spacing-200);
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  `,
  title: css`
    margin-top: var(--cds-spacing-800);
    margin-bottom: var(--cds-spacing-300);
    min-width: 0;
    text-align: center;
    ${breakpoints.down('xs')} {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  subtitle: css`
    margin-bottom: var(--cds-spacing-200);
    min-width: 0;
    text-align: center;

    ${breakpoints.down('xs')} {
      width: 100%;
      font-size: 14px;
    }
  `,
  imageContainer: css`
    display: flex;
    justify-content: center;
    margin: var(--cds-spacing-300) 0;
  `,
};

const skeletonStyles = {
  textContainer: css`
    margin-top: var(--cds-spacing-800);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--cds-spacing-200);
  `,
  image: css`
    margin-top: var(--cds-spacing-600);
    margin-bottom: var(--cds-spacing-600);
  `,
};

const PageSkeleton = ({ isMobile }: Props) => (
  <PageGridContainer>
    <Grid item container css={styles.mainBodyGrid}>
      <Grid item css={skeletonStyles.textContainer}>
        <Typography2 component="h1" variant="titleLarge">
          <Skeleton width={200} />
        </Typography2>
        <Typography2 component="p" variant="bodyPrimary">
          <Skeleton width={400} />
        </Typography2>
      </Grid>
      <Skeleton
        css={skeletonStyles.image}
        variant="circle"
        width={isMobile ? 222 : 344}
        height={isMobile ? 222 : 344}
      />
    </Grid>
  </PageGridContainer>
);

export const IntroPageContainer = ({ isMobile }: Props) => {
  const user = getUser();

  const fullName = user?.fullName;
  const firstName = fullName?.split(' ')[0];
  const dateJoined = moment(user?.date_joined);
  const isReturningUser = moment().diff(dateJoined, 'hours') > 24;

  if (!firstName) return <PageSkeleton isMobile={isMobile} />;

  return (
    <PageGridContainer>
      <Grid item container css={styles.mainBodyGrid}>
        <Grid item>
          <Typography2 component="h1" variant="titleLarge" css={styles.title}>
            {isReturningUser
              ? _t('Hi #{firstName}!', { firstName })
              : _t('Welcome to Coursera, #{firstName}!', { firstName })}
          </Typography2>
          <Typography2 component="p" variant="bodyPrimary" css={styles.subtitle}>
            {isReturningUser
              ? _t('Answer a few questions so we can personalize your experience.')
              : _t('We’re so glad you’re here. Tell us a little about yourself so we can personalize your experience.')}
          </Typography2>
        </Grid>
        <Grid item css={styles.imageContainer}>
          <Imgix
            src={`${config.url.resource_assets}onboarding_2022/intro_image.png`}
            alt={_t('Two women holding a tablet in front to a blue C Coursera logo')}
            maxWidth={isMobile ? 222 : 344}
          />
        </Grid>
      </Grid>
    </PageGridContainer>
  );
};

export default IntroPageContainer;
